import React, { useState } from 'react';
import { Grid, Box, IconButton, Typography, Switch, FormControlLabel } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import InputField from 'components/formFields/InputField';
import AutocompleteField from 'components/formFields/autocompleteField';
import { InfoModal } from 'components/generics/InfoModal';
import { useField } from 'formik';

interface Props {
  formField: any;
  servicesOptions: { value: string; label: string }[];
}

const executionModes = [
  { value: 'AUTO', label: 'Automático' },
  { value: 'MANUAL', label: 'Manual' }
];

export const AgentesForm = ({ formField, servicesOptions }: Props) => {
  const {
    name,
    description,
    service_id,
    systemPrompt,
    temperature,
    topP,
    maxTokens,
    enableTools,
    enableMemory,
    enableReasoning,
    enableFunctionCall,
    enableSearch,
    enableCodeExecution,
    executionMode
  } = formField;

  const [activeModal, setActiveModal] = useState<string | null>(null);

  const getModalContent = () => {
    switch (activeModal) {
      case 'name': return { title: 'Nombre', description: 'Nombre del Agente.', infoText: 'Dale un nombre fácil de reconocer, por ejemplo: "Asistente de Reservas".' };
      case 'description': return { title: 'Descripción', description: 'Descripción del Agente.', infoText: 'Explica brevemente qué hará este agente para que otros sepan para qué sirve.' };
      case 'service_id': return { title: 'Servicio IA', description: 'Modelo IA base (Cerebro).', infoText: 'Elige el "cerebro" que usará este agente. Algunos son más rápidos y otros analizan mejor.' };
      case 'systemPrompt': return { title: 'System Prompt (Instrucciones)', description: 'Instrucciones principales del agente.', infoText: 'Aquí le dices al agente cómo debe comportarse. Por ejemplo: "Eres un conserje amable y siempre saludas al cliente".' };
      case 'temperature': return { title: 'Temperatura', description: 'Creatividad de las respuestas.', infoText: 'Si es cercano a 0, el agente será muy directo y exacto. Si lo acercas a 2, será más creativo pero podría inventar información.' };
      case 'topP': return { title: 'Top P', description: 'Variedad de palabras.', infoText: 'Similar a la temperatura, ayuda a que el agente no sea repetitivo. Déjalo vacío si no estás seguro.' };
      case 'maxTokens': return { title: 'Máximo de Tokens', description: 'Límite de longitud de la respuesta.', infoText: 'Controla qué tan largas serán las respuestas del agente. Un valor de 2000 equivale a unas cuantas páginas.' };
      case 'enableTools': return { title: 'Habilitar Herramientas', description: 'Permite al agente usar herramientas externas.', infoText: 'Al activar esto, el agente podrá realizar acciones extra, como leer archivos de datos para ayudarte mejor.' };
      case 'enableMemory': return { title: 'Habilitar Memoria', description: 'Permite al agente recordar la conversación.', infoText: 'Activa esta opción para que el agente recuerde lo que hablaron anteriormente y mantenga el hilo de la charla.' };
      case 'enableReasoning': return { title: 'Habilitar Razonamiento', description: 'Mejora la capacidad de pensar del agente.', infoText: 'El agente se tomará un poco más de tiempo para analizar situaciones complejas y darte una respuesta más exacta.' };
      case 'enableFunctionCall': return { title: 'Llamada de Funciones', description: 'Permite ejecutar acciones en el sistema.', infoText: 'El agente podrá comunicarse directamente con otras partes del programa para realizar tareas automáticas por ti.' };
      case 'enableSearch': return { title: 'Habilitar Búsqueda', description: 'Permite al agente buscar en internet.', infoText: 'El agente podrá navegar por la web para encontrar información actualizada en tiempo real si no conoce la respuesta.' };
      case 'enableCodeExecution': return { title: 'Ejecución de Código', description: 'Permite al agente ejecutar programas simples.', infoText: 'Útil si necesitas que el agente haga cálculos matemáticos muy precisos o procese datos usando código temporal.' };
      case 'executionMode': return { title: 'Modo de Ejecución', description: 'Control de autonomía del agente.', infoText: 'Define si el agente toma acciones de manera automática o si funciona de forma manual requiriendo aprobación.' };
      default: return { title: '', description: '', infoText: '' };
    }
  };

  const modalContent = getModalContent();

  const renderFieldWithInfo = (field: React.ReactNode, modalKey: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flexGrow: 1 }}>
        {field}
      </Box>
      <IconButton color="info" onClick={() => setActiveModal(modalKey)}>
        <InfoOutlined />
      </IconButton>
    </Box>
  );

  const CustomSwitch = ({ fieldConfig, modalKey }: { fieldConfig: any; modalKey: string }) => {
    const [field, , helpers] = useField(fieldConfig.name);
    const isActive = field.value;
    
    return (
      <Box sx={{ 
          p: 1.5, 
          borderRadius: 3, 
          border: '1px solid', 
          borderColor: isActive ? 'primary.main' : 'divider',
          background: isActive ? (theme) => `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.primary.main}15 100%)` : 'background.paper',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          boxShadow: isActive ? '0px 4px 12px rgba(107, 76, 255, 0.15)' : 'none',
          '&:hover': {
              borderColor: 'primary.light',
              transform: 'translateY(-2px)'
          }
      }}>
        <FormControlLabel
          control={
            <Switch
              checked={isActive}
              onChange={(e) => helpers.setValue(e.target.checked)}
              color="primary"
            />
          }
          label={<Typography sx={{ fontWeight: isActive ? 600 : 400, transition: '0.2s' }}>{fieldConfig.label}</Typography>}
          sx={{ m: 0, flexGrow: 1 }}
        />
        <IconButton size="small" color="info" onClick={() => setActiveModal(modalKey)}>
          <InfoOutlined fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid size={{ xs:12, sm:6 }}>
          {renderFieldWithInfo(<InputField name={name.name} label={name.label} fullWidth />, 'name')}
        </Grid>
        <Grid size={{ xs:12, sm:6 }} >
          {renderFieldWithInfo(<AutocompleteField name={service_id.name} label={service_id.label} options={servicesOptions} fullWidth />, 'service_id')}
        </Grid>
        <Grid size={{ xs:12 }}>
          {renderFieldWithInfo(<InputField name={description.name} label={description.label} fullWidth multiline minRows={2} />, 'description')}
        </Grid>
        <Grid size={{ xs:12 }}>
          {renderFieldWithInfo(<InputField name={systemPrompt.name} label={systemPrompt.label} fullWidth multiline minRows={4} />, 'systemPrompt')}
        </Grid>
        <Grid size={{ xs:12, sm:4 }}>
          {renderFieldWithInfo(<InputField name={temperature.name} label={temperature.label} fullWidth type="number" inputProps={{ step: "0.1", min: "0", max: "2" }} />, 'temperature')}
        </Grid>
        <Grid size={{ xs:12, sm:4 }}>
          {renderFieldWithInfo(<InputField name={topP.name} label={topP.label} fullWidth type="number" inputProps={{ step: "0.1", min: "0", max: "1" }} />, 'topP')}
        </Grid>
        <Grid size={{ xs:12, sm:4 }}>
          {renderFieldWithInfo(<InputField name={maxTokens.name} label={maxTokens.label} fullWidth type="number" />, 'maxTokens')}
        </Grid>
        
        <Grid size={{ xs:12 }}>
          <Box sx={{ mt: 3, mb: 1, p: 2, borderRadius: 2, background: 'linear-gradient(90deg, rgba(107,76,255,0.05) 0%, rgba(0,0,0,0) 100%)', borderLeft: '4px solid #6b4cff' }}>
             <Typography variant="h6" sx={{ fontWeight: 700, background: 'linear-gradient(45deg, #6b4cff, #a855f7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                🧠 Capacidades Inteligentes
             </Typography>
             <Typography variant="body2" color="textSecondary">
                Activa o desactiva las habilidades especiales que tendrá este agente.
             </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs:12, sm:4 }}>
          <CustomSwitch fieldConfig={enableTools} modalKey="enableTools" />
        </Grid>
        <Grid size={{ xs:12, sm:4 }}>
          <CustomSwitch fieldConfig={enableMemory} modalKey="enableMemory" />
        </Grid>
        <Grid size={{ xs:12, sm:4 }}>
          <CustomSwitch fieldConfig={enableReasoning} modalKey="enableReasoning" />
        </Grid>
        <Grid size={{ xs:12, sm:4 }}>
          <CustomSwitch fieldConfig={enableFunctionCall} modalKey="enableFunctionCall" />
        </Grid>
        <Grid size={{ xs:12, sm:4 }}>
          <CustomSwitch fieldConfig={enableSearch} modalKey="enableSearch" />
        </Grid>
        <Grid size={{ xs:12, sm:4 }}>
          <CustomSwitch fieldConfig={enableCodeExecution} modalKey="enableCodeExecution" />
        </Grid>

        <Grid size={{ xs:12, sm:6 }} sx={{ mt: 2 }}>
           {renderFieldWithInfo(<AutocompleteField name={executionMode.name} label={executionMode.label} options={executionModes} fullWidth />, 'executionMode')}
        </Grid>

      </Grid>
      <InfoModal
        open={activeModal !== null}
        setOpen={(val: boolean) => { if (!val) setActiveModal(null); }}
        title={modalContent.title}
        description={modalContent.description}
        infoText={modalContent.infoText}
        icon="Info"
      />
    </>
  );
};
